/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 ***/

import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from "react";

// Import API actions for approving or denying vehicle-related documents
import {
    ApproveOrDeniedVehicleContratorDocs,
    ApproveOrDeniedVehicleSupplierDocs
} from "../../../../../reduxToolkit/ApproveAndDeniedVehiclesDocs/ApproveAndDeniedVehicleDocsApi";

import { toast } from "react-toastify";

// Enum defining different user types
import { USER_TYPE_MODEL } from "../../../../../enums/userTypeEnum";

// Import API actions for employee-related document approval/denial
import { ApproveCompanyEmployeeDocument } from "../../../../../reduxToolkit/Employee/EmployeeApi";
import { ApproveContractorDocument } from "../../../../../reduxToolkit/EmployeeContractors/EmployeeContractorsApi";
import { ApproveExternalDocument } from "../../../../../reduxToolkit/EmployeeProviders/EmployeeProvidersApi";
import {MODELS_NAME} from "../../../../../Apis/Models";
import {errorMsg} from "../../../../../Apis/functions";

/**
 * Custom hook to handle document denial logic based on document type and user role.
 *
 * @param {string} userType - The type of user (employee, contractor, supplier, etc.).
 * @param {boolean} show - Controls visibility; used to reset the comment text.
 * @param {string} documentId - The ID of the document to deny.
 * @param {string} modelVehicle - Indicates whether the document is vehicle-related.
 * @param {function} setUpdated - Function to trigger UI update after success.
 * @param {function} onHide - Function to close modal or hide component.
 */
const DenyDocumentApp = ({ userType, show, documentId, modelVehicle, setUpdated, onHide }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [commentText, setCommentText] = useState('');

    // Reset the comment field when the component is shown
    useEffect(() => {
        if (show) {
            setCommentText('');
        }
    }, [show]);

    // Main handler to process the denial action
    const handle = () => {
        const data = {
            comments: commentText,
            id: documentId,
            validated: false, // Indicates document is being denied
        };

        if (modelVehicle === MODELS_NAME.contractorVehicle) {
            dispatch(ApproveOrDeniedVehicleContratorDocs(data)).then(
                ({
                     payload: {
                         data: { success, code },
                     },
                 }) => {
                    if (success) successMsg();
                    else errorMsg(code)
                },
            );
        }
        else if (modelVehicle === MODELS_NAME.supplierVehicle){
            dispatch(ApproveOrDeniedVehicleSupplierDocs(data)).then(
                ({
                     payload: {
                         data: { data, success },
                     },
                 }) => {
                    if (success) successMsg();
                },
            );
        }
        else {
            // If it's an employee/related document, choose the right API call based on user type
            switch (userType){
                case USER_TYPE_MODEL.EMPLOYEE:
                    dispatch(ApproveCompanyEmployeeDocument(data)).then(
                        ({
                             payload: {
                                 data: { success, code },
                             },
                         }) => {
                            if (success) successMsg();
                            else errorMsg(code);
                        },
                    );
                    break;
                case USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE:
                case USER_TYPE_MODEL.CONTRACTORS_EMPLOYEE:
                    dispatch(ApproveContractorDocument(data)).then(
                        ({
                             payload: {
                                 data: { success, code },
                             },
                         }) => {
                            if (success) successMsg();
                            else errorMsg(code);
                        },
                    );
                    break;
                case USER_TYPE_MODEL.SUPPLIER_IN_CHARGE:
                case USER_TYPE_MODEL.SUPPLIER_EMPLOYEE:
                    dispatch(ApproveExternalDocument(data)).then(
                        ({
                             payload: {
                                 data: { success, code },
                             },
                         }) => {
                            if (success) successMsg();
                            else errorMsg(code);
                        },
                    );
                    break;
                default:
                    break;
            }
        }

        // Hide the modal regardless of result
        onHide();
    }

    // Function to show a success message and trigger updates
    const successMsg = () => {
        toast.success(t('denied_successfully').toUpperCase());
        setUpdated(true); // Flag to trigger parent update
        onHide(); // Close modal/dialog
    }

    // Return the translation hook, handlers, and state values for use in the UI
    return {
        t,
        handle,
        commentText,
        setCommentText
    };
};

export default DenyDocumentApp;
